import { useState, useEffect, useContext } from "react";
import request from "../../utilities/requests";
import { Box, Dialog, DialogTitle, DialogContent } from "@mui/material";
import {
  Button,
  Typography,
  ButtonGroup,
  CircularProgress,
} from "@mui/material";
import { IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Checkbox } from "@mui/material";
import { EventContext } from "../../contexts/EventContext";

const Notifications = () => {
  const { setNotificationCount} = useContext(EventContext);
  const [notifications, setNotifications] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeFilter, setActiveFilter] = useState("new");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    getNotifications("new");
  }, []);

  const formatTimeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getNotifications = async (filter = "new") => {
    setLoading(true);
    const url = filter ? `notification?filter=${filter}` : "notification";
    const result = await request(url);
    if (result.success) {
      setNotifications(result.notifications);
      setActiveFilter(filter);
    }
    setLoading(false);
  };

  const updateStatus = async (ids, status) => {
    const idArray = Array.isArray(ids) ? ids : [ids];

    const body = { ids: idArray, status };
    const result = await request("notification/status", "post", body);

    if (result.success) {
      const updated = notifications.map((n) =>
        idArray.includes(n._id) ? { ...n, status } : n
      );

      setNotifications(updated);

      // update the global notification count for "new"
      const newCount = updated.filter((n) => n.status === "new").length;
      setNotificationCount(newCount);
    }
  };

  const deleteNotification = async (id) => {
    const body = { ids: [id], status: "deleted" };
    const result = await request("notification/status", "post", body);

    if (result.success) {
      setNotifications((prev) => prev.filter((n) => n._id !== id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleOpen = (notification, isNew) => {
    setSelected(notification);
    if (isNew) {
      updateStatus(notification._id, "read");
    }
  };

  return (
    <>
      <Typography
        variant="h5"
        sx={{ p: 2, fontWeight: 600, textAlign: "center" }}
      >
        Notifications
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <ButtonGroup variant="outlined">
          <Button
            size="small"
            variant={activeFilter === "new" ? "contained" : "outlined"}
            onClick={() => getNotifications("new")}
          >
            New
          </Button>
          <Button
            size="small"
            variant={activeFilter === "read" ? "contained" : "outlined"}
            onClick={() => getNotifications("read")}
          >
            Read
          </Button>
          <Button
            size="small"
            variant={activeFilter === "all" ? "contained" : "outlined"}
            onClick={() => getNotifications("all")}
          >
            All
          </Button>
        </ButtonGroup>
      </Box>

      {selectedIds.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, p: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              updateStatus(selectedIds, "read");
              setSelectedIds([]);
            }}
          >
            Mark as Read
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => {
              updateStatus(selectedIds, "deleted");
              setNotifications((prev) =>
                prev.filter((n) => !selectedIds.includes(n._id))
              );
              setSelectedIds([]);
            }}
          >
            Delete
          </Button>
        </Box>
      )}

      <Box sx={{ p: 2, textAlign: "center" }}>
        {loading && <CircularProgress />}

        {!loading && notifications.length === 0 && (
          <Typography sx={{ mt: 2 }}>
            All caught up! No messages to display.
          </Typography>
        )}

        {!loading &&
          notifications.map((n) => {
            let borderColor = "green";
            if (n.level === "warning") borderColor = "goldenrod";
            if (n.level === "error") borderColor = "red";

            const isNew = n.status === "new";

            return (
              <Box
                key={n._id}
                onClick={() => handleOpen(n, isNew)}
                sx={{
                  p: 2,
                  mb: 2,
                  border: `2px solid ${borderColor}`,
                  borderRadius: "6px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: isNew ? "#eef6ff" : "transparent",
                  fontWeight: isNew ? 600 : 400,
                  opacity: isNew ? 1 : 0.6,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    checked={selectedIds.includes(n._id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelect(n._id)}
                    size="small"
                  />
                  <span style={{ fontWeight: isNew ? 600 : 400 }}>
                    {n.title}
                  </span>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span style={{ opacity: 0.7 }}>
                    {formatTimeAgo(n.createdAt)}
                  </span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(n._id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
      </Box>

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="sm"
      >
        {selected && (
          <>
            <DialogTitle
              sx={{
                fontWeight: 600,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {selected.title}
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  deleteNotification(selected._id);
                  setSelected(null);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent
              sx={{
                fontSize: "1rem",
                lineHeight: 1.5,
                p: 3,
              }}
            >
              <p
                style={{
                  marginBottom: "1rem",
                  whiteSpace: "pre-line",
                }}
              >
                {selected.message}
              </p>

              <p
                style={{
                  opacity: 0.6,
                  fontSize: "0.85rem",
                  marginBottom: 0,
                }}
              >
                Received: {new Date(selected.createdAt).toLocaleString()}
              </p>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

export default Notifications;
